import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import ContactContext from 'contexts/contactContext'
import useEnabledLuxPlusBenefits from 'luxPlus/hooks/useEnabledLuxPlusBenefits'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { DEFAULT_BENEFIT_DESCRIPTION } from 'luxPlus/constants/benefits'
import groupLuxPlusBenefits from 'luxPlus/lib/groupLuxPlusBenefits'
import { sortBy } from 'lib/array/arrayUtils'

/**
 * Controls which benefit types are displayed and in what order.
 * Group types (e.g. 'partner_promos', 'cruise_benefits') must match the `groups` arg
 * to `groupLuxPlusBenefits` below — the group replaces its member types in the stream,
 * so member types (e.g. 'partner_promo_code', 'cruise_inclusions') should NOT appear here.
 */
const BENEFIT_DISPLAY_ORDER: Array<App.LuxPlusBenefitViewType> = [
  'discounted_flash',
  'discounted_flights',
  'discounted_experiences',
  'hotel_inclusions',
  'exclusive_offers',
  'early_access',
  'discounted_tours',
  'partner_promos',
  'cruise_benefits',
  'promo',
  'priority_support',
]

/**
 * Returns a display-ready, ordered list of LuxPlus+ benefit views.
 * Resolves enabled benefits from the subscription product, strips default
 * descriptions, injects dynamic content (e.g. priority support phone number),
 * groups related items (e.g. partner promos), then filters and sorts by
 * BENEFIT_DISPLAY_ORDER.
 */
function useLuxPlusBenefitsView(): Array<App.LuxPlusBenefitView> {
  const intl = useIntl()
  const supportContact = useContext(ContactContext)
  const subscriptionProduct = useAppSelector(state => state.luxPlus.products.subscription)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const offer = useMemo(() =>
    subscriptionProduct.offers?.find(o => !o.joinFeeWaived),
  [subscriptionProduct.offers])

  const enabledBenefits = useEnabledLuxPlusBenefits(offer?.benefits)

  const humanReadablePhone = supportContact.local.humanReadable
  const prioritySupportDescription = intl.formatMessage(
    {
      id: 'hHD0rGq2lFRGZxgozfjE',
      defaultMessage: 'Call {phoneNumber}',
      description: 'Priority support benefit description with phone number',
    },
    { phoneNumber: humanReadablePhone },
  )

  const resolveDynamicBenefits = useCallback((benefit: App.LuxPlusBenefitItem): App.LuxPlusBenefitItem => {
    if (benefit.type === 'priority_support') {
      if (canRedeemLuxPlusBenefits && humanReadablePhone) {
        return { ...benefit, description: prioritySupportDescription }
      }
    }
    return benefit
  }, [canRedeemLuxPlusBenefits, humanReadablePhone, prioritySupportDescription])

  return useMemo(() => {
    const orderIndex = new Map(BENEFIT_DISPLAY_ORDER.map((type, i) => [type, i]))
    const prepared = enabledBenefits
      .map(benefit => {
        // Undefine description if placeholder
        const normalised = benefit.description?.trim().toLowerCase()
        const isPlaceholder = !!normalised && normalised === DEFAULT_BENEFIT_DESCRIPTION.toLowerCase()
        return isPlaceholder ? { ...benefit, description: undefined } : benefit
      })
      .map(resolveDynamicBenefits)
    const views = groupLuxPlusBenefits(prepared, intl, ['partner_promos', 'cruise_benefits'])
    return sortBy(
      views.filter(benefit => orderIndex.has(benefit.type)),
      benefit => orderIndex.get(benefit.type) ?? BENEFIT_DISPLAY_ORDER.length,
      'asc',
    )
  }, [enabledBenefits, intl, resolveDynamicBenefits])
}

export default useLuxPlusBenefitsView
